/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { fetchAllRows } from "../../../shared/utils/fetchAllRows.js";

// Depletions store a month number; forecasts store the month name. Actuals are
// normalised onto the forecast's month names so the two line up on one axis.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// =====================================================================
// forecastService :: forecasting data over RLS (Supabase Auth)
//
// Ported from the source app, where every call went through a service-role
// `forecasting` edge function (Azure-AD model). Brand Command uses Supabase
// Auth + RLS, so — like mapsService / accountService — we talk to the
// tables DIRECTLY with the authenticated client. RLS (migration 0021) pins
// every row to the caller's account via is_account_member(account_id).
//
// The shapes returned here match exactly what Dashboard.jsx / ForecastGrid /
// ForecastChart expect ({ states, products, rawForecast } with a `fields`
// envelope per forecast row), so the ported components are unchanged.
//
// Writes use an explicit account_id (from the caller's primary workspace)
// so the RLS WITH CHECK passes; DB-controlled columns are never sent.
// =====================================================================

// ── Active account resolution (cached for the session) ──────────────────────
// ⚠️ Resolve the tenant through the SHARED getMyPrimaryAccount() and nothing
// else. This used to re-implement the pick locally (owner → admin → first
// joined) off `account_members`, which ignored the workspace switcher's stored
// choice — so a user in two workspaces who switched to the second one still got
// the FIRST one's forecasts. Duplicating the rule is how the copies drift.
let _accountIdPromise = null;

export function resetActiveAccount() {
  _accountIdPromise = null;
}

async function activeAccountId() {
  if (_accountIdPromise) return _accountIdPromise;
  _accountIdPromise = (async () => {
    const acct = await getMyPrimaryAccount();
    if (!acct?.accountId) throw new Error("No workspace found for your account.");
    return acct.accountId;
  })();
  return _accountIdPromise;
}

// ── Loaders ─────────────────────────────────────────────────────────────────

/**
 * Load everything the Forecast Dashboard needs.
 * Returns: { states, products, rawForecast }
 *   states:   [{ id, state_name, region, distributor }]
 *   products: [{ id, name, active, tag, sizes:{name}, flavors:{name}, pack_sizes:{name} }]
 *   rawForecast: [{ id, fields: { Year, Month, State, Region, Distributor,
 *                  ProductSKU, CaseForecast, ModifiedByDisplay, Modified,
 *                  _stateId, _productId, _sizeName } }]
 */
export async function loadForecastData() {
  const acct = await activeAccountId();

  // A full year of forecast is states x SKUs x 12 and blows past PostgREST's
  // 1000-row cap on any real portfolio — every read here is paginated.
  const [statesRes, productsRes, forecastRes] = await Promise.allSettled([
    fetchAllRows(() => supabase
      .from("sales_states")
      .select("id, state_name, region, distributor, active")
      .eq("account_id", acct)
      .order("state_name", { ascending: true }), { label: "sales_states" }),
    fetchAllRows(() => supabase
      .from("products")
      .select("id, name, active, tag, flavor_id, size_id, pack_size_id, sizes:product_sizes(name), flavors:product_flavors(name), pack_sizes:pack_sizes(name)")
      .eq("account_id", acct)
      .order("name", { ascending: true }), { label: "products" }),
    fetchAllRows(() => supabase
      .from("forecasts")
      .select("id, state_id, product_id, year, month, case_forecast, updated_by, updated_at")
      .eq("account_id", acct)
      .order("id", { ascending: true }), { label: "forecasts" }),
  ]);

  if (statesRes.status   === "rejected") { console.error("[forecastService.loadForecastData:states]", statesRes.reason);   throw new Error("Could not load states."); }
  if (productsRes.status === "rejected") { console.error("[forecastService.loadForecastData:products]", productsRes.reason); throw new Error("Could not load products."); }
  if (forecastRes.status === "rejected") { console.error("[forecastService.loadForecastData:forecasts]", forecastRes.reason); throw new Error("Could not load forecasts."); }

  const states   = statesRes.value ?? [];
  const products = productsRes.value ?? [];

  const stateById = new Map(states.map((s) => [s.id, s]));
  const prodById  = new Map(products.map((p) => [p.id, p]));

  const rawForecast = (forecastRes.value ?? []).map((r) => {
    const s = stateById.get(r.state_id) || {};
    const p = prodById.get(r.product_id) || {};
    return {
      id: r.id,
      fields: {
        Year:              String(r.year),
        Month:             r.month,
        State:             s.state_name ?? "",
        Region:            s.region ?? "",
        Distributor:       s.distributor ?? "",
        ProductSKU:        p.name ?? "",
        CaseForecast:      r.case_forecast ?? 0,
        ModifiedByDisplay: r.updated_by ?? "",
        Modified:          r.updated_at ?? "",
        _stateId:          r.state_id,
        _productId:        r.product_id,
        _sizeName:         p.sizes?.name ?? "",
      },
    };
  });

  // ── ACTUALS (cross-module read) ──────────────────────────────────────────
  // Depletions are OWNED by the depletions-inventory module. Plan-vs-Actual
  // needs them, so this is a read-only, RLS-scoped query against that module's
  // table — the same documented pattern distributor-orders uses to read this
  // module's `forecasts`. See ARCHITECTURE.md "Cross-module dependencies".
  //
  // Depletions key on (flavor, size, pack); forecasts key on product_id. They
  // meet on that triple, which is exactly what defines a SKU.
  const skuByDimensions = new Map();
  for (const prod of products) {
    if (!prod?.name) continue;
    skuByDimensions.set(`${prod.flavor_id}|${prod.size_id}|${prod.pack_size_id}`, prod.name);
  }

  let depletionRows = [];
  try {
    depletionRows = await fetchAllRows(
      () => supabase
        .from("depletions")
        .select("year, month, state_id, flavor_id, size_id, pack_size_id, case_sales")
        .eq("account_id", acct)
        .order("id", { ascending: true }),
      { label: "depletions (actuals)" }
    );
  } catch (err) {
    // Actuals are additive — the forecast still works without them.
    console.error("[forecastService.loadForecastData:actuals]", err);
  }

  const rawActuals = depletionRows.map((r) => {
    const st = stateById.get(r.state_id) || {};
    const sku = skuByDimensions.get(`${r.flavor_id}|${r.size_id}|${r.pack_size_id}`) ?? "";
    return {
      fields: {
        Year:        String(r.year),
        Month:       MONTH_NAMES[(Number(r.month) || 0) - 1] ?? "",
        State:       st.state_name ?? "",
        Region:      st.region ?? "",
        Distributor: st.distributor ?? "",
        ProductSKU:  sku,
        CaseActual:  Number(r.case_sales) || 0,
      },
    };
  });

  return { states, products, rawForecast, rawActuals };
}

// ── Writes (whitelisted columns; account-scoped; generic errors) ─────────────

/** Update the case_forecast value for an existing forecast row. */
export async function updateForecastCase(id, caseForecast, updatedBy) {
  const value = Number(caseForecast);
  if (!Number.isFinite(value) || value < 0) throw new Error("Invalid forecast value.");

  const { error } = await supabase
    .from("forecasts")
    .update({ case_forecast: value, updated_by: updatedBy ?? null })
    .eq("id", id);
  if (error) {
    console.error("[forecastService.updateForecastCase]", error);
    throw new Error("Could not save the forecast.");
  }
  return { id };
}

/** Delete a forecast row by id (used when a cell is cleared, and for undo). */
export async function deleteForecastRow(id) {
  const { error } = await supabase.from("forecasts").delete().eq("id", id);
  if (error) {
    console.error("[forecastService.deleteForecastRow]", error);
    throw new Error("Could not delete the forecast.");
  }
  return { id };
}

/**
 * Create a new forecast row.
 * `month` is the full English month name ("January") — stored as-is.
 * Returns the inserted row's { id, updated_at }.
 */
export async function createForecastRow({ stateId, productId, year, month, caseForecast, updatedBy }) {
  const value = Number(caseForecast);
  if (!stateId || !productId) throw new Error("Missing state or product.");
  if (!Number.isInteger(Number(year))) throw new Error("Invalid year.");
  if (!month) throw new Error("Missing month.");
  if (!Number.isFinite(value) || value < 0) throw new Error("Invalid forecast value.");

  const acct = await activeAccountId();

  const { data, error } = await supabase
    .from("forecasts")
    .insert({
      account_id:    acct,
      state_id:      stateId,
      product_id:    productId,
      year:          Number(year),
      month,
      case_forecast: value,
      updated_by:    updatedBy ?? null,
    })
    .select("id, updated_at")
    .single();

  if (error) {
    console.error("[forecastService.createForecastRow]", error);
    if (error.code === "23505") throw new Error("A forecast already exists for that cell.");
    throw new Error("Could not create the forecast.");
  }
  return { id: data.id, updated_at: data.updated_at };
}

// ── Bulk cell writes (the year seed) ────────────────────────────────────────
// APP-522. The seed used to run one createForecastRow / updateForecastCase per
// cell — 240 round trips for a 20-SKU year. These two calls replace all of them
// with two: read what the (state, year) actually holds, then upsert the whole
// block in one statement.
//
// The pre-read is not an optimisation, it is what keeps UNDO exact. The undo
// record has to say, per cell, "this row did not exist before, delete it" or
// "it held N, put N back" — and the only trustworthy source for that is the DB
// at write time. The old per-cell path got it for free (an INSERT that hit
// `forecasts_unique_cell` failed loudly, so a pre-existing row was never
// mistaken for one we created); an upsert updates silently instead, so a stale
// in-memory grid would have undo DELETE a row the user typed by hand.

/**
 * Every forecast cell that already exists for one (state, year), as the DB
 * holds it right now.
 *
 * `strict` on purpose: a truncated page here reads as "those cells are empty",
 * which is the one error that makes undo destructive.
 *
 * @returns {Promise<Array<{ id, productId, month, caseForecast }>>}
 */
export async function fetchForecastCells({ stateId, year }) {
  if (!stateId) throw new Error("Missing state.");
  if (!Number.isInteger(Number(year))) throw new Error("Invalid year.");
  const acct = await activeAccountId();

  let rows;
  try {
    rows = await fetchAllRows(
      () => supabase
        .from("forecasts")
        .select("id, product_id, month, case_forecast")
        .eq("account_id", acct)
        .eq("state_id", stateId)
        .eq("year", Number(year))
        .order("id", { ascending: true }),
      { label: "forecasts (seed pre-read)", strict: true }
    );
  } catch (err) {
    console.error("[forecastService.fetchForecastCells]", err);
    throw new Error("Could not read the existing forecast.");
  }

  return rows.map((r) => ({
    id: r.id,
    productId: r.product_id,
    month: r.month,
    caseForecast: Number(r.case_forecast) || 0,
  }));
}

// One statement per chunk. 12 months x SKUs for a single state is a few hundred
// rows at worst, but a chunk keeps one pathological workspace from posting a
// multi-megabyte body — and a chunk that fails leaves the chunks that landed
// fully recorded, so undo stays exact after a partial run.
const UPSERT_CHUNK = 500;

/**
 * Insert-or-update a block of forecast cells for ONE (state, year) in a single
 * round trip per chunk, and return the rows as the DB now holds them.
 *
 * Columns are enumerated here, not taken from the caller: `id`, `created_at`,
 * `updated_at` and `account_id` are DB-controlled and never accepted from a
 * cell.
 *
 * @param {object} a
 * @param {string} a.stateId
 * @param {number|string} a.year
 * @param {Array<{ productId, month, caseForecast }>} a.cells
 * @param {string} [a.updatedBy]
 * @returns {Promise<{ rows: Array<{ id, productId, month, caseForecast, updatedAt }>, failed: number }>}
 */
export async function bulkUpsertForecastCells({ stateId, year, cells = [], updatedBy }) {
  if (!stateId) throw new Error("Missing state.");
  if (!Number.isInteger(Number(year))) throw new Error("Invalid year.");
  if (!cells.length) return { rows: [], failed: 0 };

  const acct = await activeAccountId();

  const payload = cells.map((c) => {
    const value = Number(c.caseForecast);
    if (!c.productId) throw new Error("Missing product.");
    if (!c.month) throw new Error("Missing month.");
    if (!Number.isFinite(value) || value < 0) throw new Error("Invalid forecast value.");
    return {
      account_id:    acct,
      state_id:      stateId,
      product_id:    c.productId,
      year:          Number(year),
      month:         c.month,
      case_forecast: value,
      updated_by:    updatedBy ?? null,
    };
  });

  const rows = [];
  let failed = 0;
  let lastError = null;

  for (let i = 0; i < payload.length; i += UPSERT_CHUNK) {
    const chunk = payload.slice(i, i + UPSERT_CHUNK);
    const { data, error } = await supabase
      .from("forecasts")
      // The cell's natural key, i.e. the `forecasts_unique_cell` constraint
      // (migration 0021). Naming the columns rather than the constraint is what
      // PostgREST's `on_conflict` takes.
      .upsert(chunk, { onConflict: "account_id,state_id,product_id,year,month" })
      .select("id, product_id, month, case_forecast, updated_at");

    if (error) {
      lastError = error;
      failed += chunk.length;
      console.error("[forecastService.bulkUpsertForecastCells]", error);
      continue;
    }
    (data ?? []).forEach((r) => rows.push({
      id: r.id,
      productId: r.product_id,
      month: r.month,
      caseForecast: Number(r.case_forecast) || 0,
      updatedAt: r.updated_at ?? null,
    }));
  }

  if (!rows.length && lastError) throw new Error("Could not save the forecast.");
  return { rows, failed };
}
